import { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  groupDemoNegativesByAdGroup,
  type DemoNegativeTarget,
} from '@/api/workspaceBulkActions';

function ModalShell({
  title,
  description,
  children,
  wide,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
  wide?: boolean;
}) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
      <div
        className={`flex w-full flex-col rounded-xl bg-surface p-5 shadow-xl ${wide ? 'max-w-2xl' : 'max-w-lg'}`}
      >
        <h3 className="text-base font-semibold text-foreground">{title}</h3>
        {description ? <p className="mt-1 text-sm text-foreground-muted">{description}</p> : null}
        <div className="mt-4">{children}</div>
      </div>
    </div>
  );
}

function ModalActions({
  pending,
  confirmLabel,
  confirmDisabled,
  onCancel,
  onConfirm,
}: {
  pending: boolean;
  confirmLabel: string;
  confirmDisabled?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="mt-4 flex justify-end gap-2">
      <Button variant="secondary" onClick={onCancel} disabled={pending}>
        Cancel
      </Button>
      <Button onClick={onConfirm} disabled={pending || confirmDisabled}>
        {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : confirmLabel}
      </Button>
    </div>
  );
}

const DEMO_AUDIENCE_OPTIONS = [
  { id: 'new_to_brand', label: 'New to brand' },
  { id: 'views_remarketing', label: 'Views remarketing' },
  { id: 'purchases_remarketing', label: 'Purchases remarketing' },
  { id: 'similar_audiences', label: 'Similar audiences' },
];

export type DemoBidMode =
  | 'SET'
  | 'INCREASE_AMOUNT'
  | 'INCREASE_PERCENT'
  | 'DECREASE_AMOUNT'
  | 'DECREASE_PERCENT';

export function WorkspaceDemoNegativeModal({
  targets,
  pending,
  onCancel,
  onConfirm,
}: {
  targets: DemoNegativeTarget[];
  pending: boolean;
  onCancel: () => void;
  onConfirm: (matchType: 'NEGATIVE_EXACT' | 'NEGATIVE_PHRASE') => void;
}) {
  const [matchType, setMatchType] = useState<'NEGATIVE_EXACT' | 'NEGATIVE_PHRASE'>('NEGATIVE_EXACT');
  const groups = useMemo(() => groupDemoNegativesByAdGroup(targets), [targets]);
  const termCount = groups.reduce((sum, group) => sum + group.terms.length, 0);

  return (
    <ModalShell
      title="Add to Negative Keywords"
      description={`Add ${termCount} term${termCount === 1 ? '' : 's'} as negatives across ${groups.length} ad group${groups.length === 1 ? '' : 's'} (demo).`}
      wide
    >
      <div className="mb-3 max-h-64 space-y-3 overflow-y-auto rounded-lg border border-border p-3">
        {groups.length === 0 ? (
          <p className="text-sm text-foreground-muted">No selected terms to negate.</p>
        ) : (
          groups.map((group) => (
            <div key={`${group.campaignId}|${group.adGroupId}`}>
              <p className="text-sm font-medium text-foreground">
                {group.campaignName}
                <span className="font-normal text-foreground-muted"> · {group.adGroupName}</span>
              </p>
              <p className="mt-0.5 text-xs text-foreground-muted">
                {group.terms.length} term{group.terms.length === 1 ? '' : 's'}
              </p>
              <ul className="mt-1 list-inside list-disc text-sm text-foreground">
                {group.terms.slice(0, 8).map((term) => (
                  <li key={term}>{term}</li>
                ))}
                {group.terms.length > 8 ? (
                  <li className="text-foreground-muted">+{group.terms.length - 8} more</li>
                ) : null}
              </ul>
            </div>
          ))
        )}
      </div>

      <label htmlFor="demo-negative-match" className="mb-1 block text-xs text-foreground-muted">
        Negative match type
      </label>
      <select
        id="demo-negative-match"
        value={matchType}
        onChange={(event) =>
          setMatchType(event.target.value as 'NEGATIVE_EXACT' | 'NEGATIVE_PHRASE')
        }
        className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm"
      >
        <option value="NEGATIVE_EXACT">Negative exact</option>
        <option value="NEGATIVE_PHRASE">Negative phrase</option>
      </select>

      <ModalActions
        pending={pending}
        confirmLabel="Add negatives"
        confirmDisabled={groups.length === 0}
        onCancel={onCancel}
        onConfirm={() => onConfirm(matchType)}
      />
    </ModalShell>
  );
}

export function WorkspaceDemoBidModal({
  selectionCount,
  entityLabel,
  pending,
  onCancel,
  onConfirm,
}: {
  selectionCount: number;
  entityLabel: string;
  pending: boolean;
  onCancel: () => void;
  onConfirm: (mode: DemoBidMode, value: number) => void;
}) {
  const [mode, setMode] = useState<DemoBidMode>('SET');
  const [value, setValue] = useState('');
  const numeric = Number(value);
  const valid = Number.isFinite(numeric) && numeric > 0;

  return (
    <ModalShell
      title="Change Bid"
      description={`Adjust bids for ${selectionCount} selected ${entityLabel}${selectionCount === 1 ? '' : 's'} (demo).`}
    >
      <div className="space-y-3">
        <div>
          <label htmlFor="demo-bid-mode" className="mb-1 block text-xs text-foreground-muted">
            Bid adjustment
          </label>
          <select
            id="demo-bid-mode"
            value={mode}
            onChange={(event) => setMode(event.target.value as DemoBidMode)}
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm"
          >
            <option value="SET">Set bid to</option>
            <option value="INCREASE_AMOUNT">Increase bid by value</option>
            <option value="INCREASE_PERCENT">Increase by %</option>
            <option value="DECREASE_AMOUNT">Decrease bid by value</option>
            <option value="DECREASE_PERCENT">Decrease by %</option>
          </select>
        </div>
        <div>
          <label htmlFor="demo-bid-value" className="mb-1 block text-xs text-foreground-muted">
            Value
          </label>
          <Input
            id="demo-bid-value"
            type="number"
            min="0"
            step={mode.endsWith('PERCENT') ? '1' : '0.01'}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            placeholder={mode.endsWith('PERCENT') ? 'Percent' : 'Bid amount'}
          />
        </div>
      </div>
      <ModalActions
        pending={pending}
        confirmLabel="Apply bid change"
        confirmDisabled={!valid}
        onCancel={onCancel}
        onConfirm={() => onConfirm(mode, numeric)}
      />
    </ModalShell>
  );
}

export function WorkspaceDemoStatusModal({
  selectionCount,
  entityLabel,
  pending,
  onCancel,
  onConfirm,
}: {
  selectionCount: number;
  entityLabel: string;
  pending: boolean;
  onCancel: () => void;
  onConfirm: (state: 'ENABLED' | 'PAUSED') => void;
}) {
  const [nextState, setNextState] = useState<'ENABLED' | 'PAUSED'>('PAUSED');

  return (
    <ModalShell
      title="Change Status"
      description={`Apply a new status to ${selectionCount} selected ${entityLabel}${selectionCount === 1 ? '' : 's'} (demo).`}
    >
      <label htmlFor="demo-status-action" className="mb-1 block text-xs text-foreground-muted">
        New status
      </label>
      <select
        id="demo-status-action"
        value={nextState}
        onChange={(event) => setNextState(event.target.value as 'ENABLED' | 'PAUSED')}
        className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm"
      >
        <option value="ENABLED">Enabled</option>
        <option value="PAUSED">Paused</option>
      </select>
      <ModalActions
        pending={pending}
        confirmLabel="Apply changes"
        onCancel={onCancel}
        onConfirm={() => onConfirm(nextState)}
      />
    </ModalShell>
  );
}

const DEMO_PLACEMENT_FIELDS = [
  { id: 'TOP_OF_SEARCH', label: 'Top of search' },
  { id: 'PRODUCT_PAGES', label: 'Product pages' },
  { id: 'REST_OF_SEARCH', label: 'Rest of search' },
] as const;

export type DemoPlacementId = (typeof DEMO_PLACEMENT_FIELDS)[number]['id'];

export type DemoPlacementAdjustments = Partial<Record<DemoPlacementId, number>>;

export function WorkspaceDemoPlacementModal({
  selectionCount,
  pending,
  onCancel,
  onConfirm,
}: {
  selectionCount: number;
  pending: boolean;
  onCancel: () => void;
  onConfirm: (adjustments: DemoPlacementAdjustments) => void;
}) {
  const [values, setValues] = useState<Record<DemoPlacementId, string>>({
    TOP_OF_SEARCH: '',
    PRODUCT_PAGES: '',
    REST_OF_SEARCH: '',
  });

  const adjustments = useMemo(() => {
    const next: DemoPlacementAdjustments = {};
    for (const field of DEMO_PLACEMENT_FIELDS) {
      const raw = values[field.id].trim();
      if (!raw) continue;
      const numeric = Number(raw);
      if (!Number.isFinite(numeric)) continue;
      next[field.id] = numeric;
    }
    return next;
  }, [values]);

  const hasInvalid = DEMO_PLACEMENT_FIELDS.some((field) => {
    const raw = values[field.id].trim();
    if (!raw) return false;
    return !Number.isFinite(Number(raw));
  });
  const canApply = Object.keys(adjustments).length > 0 && !hasInvalid;

  return (
    <ModalShell
      title="Change Placement"
      description={`Update placement bid adjustments for ${selectionCount} selected campaign${selectionCount === 1 ? '' : 's'} (demo). Leave a field blank to keep it unchanged.`}
    >
      <div className="space-y-3">
        {DEMO_PLACEMENT_FIELDS.map((field) => (
          <div key={field.id}>
            <label
              htmlFor={`demo-placement-${field.id}`}
              className="mb-1 block text-xs text-foreground-muted"
            >
              {field.label} (%)
            </label>
            <Input
              id={`demo-placement-${field.id}`}
              type="number"
              value={values[field.id]}
              onChange={(event) =>
                setValues((prev) => ({ ...prev, [field.id]: event.target.value }))
              }
              placeholder="Unchanged"
            />
          </div>
        ))}
      </div>
      <ModalActions
        pending={pending}
        confirmLabel="Apply placement"
        confirmDisabled={!canApply}
        onCancel={onCancel}
        onConfirm={() => onConfirm(adjustments)}
      />
    </ModalShell>
  );
}

export function WorkspaceDemoBidBoostModal({
  selectionCount,
  pending,
  onCancel,
  onConfirm,
}: {
  selectionCount: number;
  pending: boolean;
  onCancel: () => void;
  onConfirm: (boostPercent: number) => void;
}) {
  const [value, setValue] = useState('15');
  const numeric = Number(value);
  const valid = Number.isFinite(numeric) && numeric > 0;

  return (
    <ModalShell
      title="Bid Boost"
      description={`Apply a bid boost to ${selectionCount} selected campaign${selectionCount === 1 ? '' : 's'} (demo).`}
    >
      <label htmlFor="demo-bid-boost" className="mb-1 block text-xs text-foreground-muted">
        Boost (%)
      </label>
      <Input
        id="demo-bid-boost"
        type="number"
        min="0"
        value={value}
        onChange={(event) => setValue(event.target.value)}
      />
      <ModalActions
        pending={pending}
        confirmLabel="Apply boost"
        confirmDisabled={!valid}
        onCancel={onCancel}
        onConfirm={() => onConfirm(numeric)}
      />
    </ModalShell>
  );
}

export function WorkspaceDemoAudienceModal({
  mode,
  selectionCount,
  pending,
  onCancel,
  onConfirm,
}: {
  mode: 'remove' | 'add_sd';
  selectionCount: number;
  pending: boolean;
  onCancel: () => void;
  onConfirm: (audienceIds: string[]) => void;
}) {
  const [selected, setSelected] = useState<string[]>([DEMO_AUDIENCE_OPTIONS[0].id]);

  const toggle = (id: string) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  return (
    <ModalShell
      title={mode === 'remove' ? 'Remove Audience' : 'Add Audience to SD campaigns'}
      description={
        mode === 'remove'
          ? `Remove audiences from ${selectionCount} selected campaign${selectionCount === 1 ? '' : 's'} (demo).`
          : `Add audiences to ${selectionCount} selected SD campaign${selectionCount === 1 ? '' : 's'} (demo).`
      }
    >
      <div className="space-y-2">
        {DEMO_AUDIENCE_OPTIONS.map((option) => (
          <label key={option.id} className="flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={selected.includes(option.id)}
              onChange={() => toggle(option.id)}
            />
            {option.label}
          </label>
        ))}
      </div>
      <ModalActions
        pending={pending}
        confirmLabel={mode === 'remove' ? 'Remove audiences' : 'Add audiences'}
        confirmDisabled={selected.length === 0}
        onCancel={onCancel}
        onConfirm={() => onConfirm(selected)}
      />
    </ModalShell>
  );
}

export function WorkspaceDemoPortfolioModal({
  selectionCount,
  portfolios,
  pending,
  onCancel,
  onConfirm,
}: {
  selectionCount: number;
  portfolios: Array<{ id: string; name: string }>;
  pending: boolean;
  onCancel: () => void;
  onConfirm: (portfolioId: string) => void;
}) {
  const options =
    portfolios.length > 0
      ? portfolios
      : [
          { id: 'demo-portfolio-1', name: 'Demo Portfolio A' },
          { id: 'demo-portfolio-2', name: 'Demo Portfolio B' },
        ];
  const [portfolioId, setPortfolioId] = useState(options[0]?.id ?? '');

  return (
    <ModalShell
      title="Set Portfolio"
      description={`Assign a portfolio to ${selectionCount} selected campaign${selectionCount === 1 ? '' : 's'} (demo).`}
    >
      <label htmlFor="demo-portfolio" className="mb-1 block text-xs text-foreground-muted">
        Portfolio
      </label>
      <select
        id="demo-portfolio"
        value={portfolioId}
        onChange={(event) => setPortfolioId(event.target.value)}
        className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm"
      >
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
      </select>
      <ModalActions
        pending={pending}
        confirmLabel="Set portfolio"
        confirmDisabled={!portfolioId}
        onCancel={onCancel}
        onConfirm={() => onConfirm(portfolioId)}
      />
    </ModalShell>
  );
}

export function WorkspaceDemoTagModal({
  mode,
  selectionCount,
  entityLabel,
  pending,
  onCancel,
  onConfirm,
}: {
  mode: 'add' | 'edit';
  selectionCount: number;
  entityLabel: string;
  pending: boolean;
  onCancel: () => void;
  onConfirm: (tag: string) => void;
}) {
  const [tag, setTag] = useState('');

  return (
    <ModalShell
      title={mode === 'add' ? 'Add Tag' : 'Edit Tags'}
      description={
        mode === 'add'
          ? `Add a tag to ${selectionCount} selected ${entityLabel}${selectionCount === 1 ? '' : 's'} (demo).`
          : `Replace tags on ${selectionCount} selected ${entityLabel}${selectionCount === 1 ? '' : 's'} (demo).`
      }
    >
      <label htmlFor="demo-tag" className="mb-1 block text-xs text-foreground-muted">
        {mode === 'add' ? 'Tag name' : 'Tags (comma-separated)'}
      </label>
      <Input
        id="demo-tag"
        value={tag}
        onChange={(event) => setTag(event.target.value)}
        placeholder={mode === 'add' ? 'e.g. high-intent' : 'e.g. brand, evergreen'}
      />
      <ModalActions
        pending={pending}
        confirmLabel={mode === 'add' ? 'Add tag' : 'Save tags'}
        confirmDisabled={!tag.trim()}
        onCancel={onCancel}
        onConfirm={() => onConfirm(tag.trim())}
      />
    </ModalShell>
  );
}

export function WorkspaceDemoAddToCampaignModal({
  selectionCount,
  campaigns,
  adGroups,
  pending,
  onCancel,
  onConfirm,
}: {
  selectionCount: number;
  campaigns: Array<{ id: string; name: string }>;
  adGroups: Array<{ id: string; name: string; campaignId: string }>;
  pending: boolean;
  onCancel: () => void;
  onConfirm: (campaignId: string, adGroupId: string) => void;
}) {
  const campaignOptions =
    campaigns.length > 0
      ? campaigns
      : [{ id: 'demo-campaign-1', name: 'Demo Campaign' }];
  const [campaignId, setCampaignId] = useState(campaignOptions[0]?.id ?? '');
  const filteredAdGroups = adGroups.filter((ag) => ag.campaignId === campaignId);
  const adGroupOptions =
    filteredAdGroups.length > 0
      ? filteredAdGroups
      : adGroups.length > 0
        ? adGroups
        : [{ id: 'demo-adgroup-1', name: 'Demo Ad Group', campaignId }];
  const [adGroupId, setAdGroupId] = useState(adGroupOptions[0]?.id ?? '');

  return (
    <ModalShell
      title="Add to Campaign"
      description={`Add ${selectionCount} selected search term${selectionCount === 1 ? '' : 's'} as keywords (demo).`}
    >
      <div className="space-y-3">
        <div>
          <label htmlFor="demo-add-campaign" className="mb-1 block text-xs text-foreground-muted">
            Campaign
          </label>
          <select
            id="demo-add-campaign"
            value={campaignId}
            onChange={(event) => {
              const next = event.target.value;
              setCampaignId(next);
              const nextAdGroups = adGroups.filter((ag) => ag.campaignId === next);
              setAdGroupId(nextAdGroups[0]?.id ?? adGroups[0]?.id ?? 'demo-adgroup-1');
            }}
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm"
          >
            {campaignOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="demo-add-adgroup" className="mb-1 block text-xs text-foreground-muted">
            Ad group
          </label>
          <select
            id="demo-add-adgroup"
            value={adGroupId}
            onChange={(event) => setAdGroupId(event.target.value)}
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm"
          >
            {adGroupOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <ModalActions
        pending={pending}
        confirmLabel="Add to campaign"
        confirmDisabled={!campaignId || !adGroupId}
        onCancel={onCancel}
        onConfirm={() => onConfirm(campaignId, adGroupId)}
      />
    </ModalShell>
  );
}
